import { NumericFormat } from 'react-number-format';
import { NUMERIC_CONFIG } from '@/shared/config';
import { type ComponentProps } from 'react';
import { Input } from '@/shared/components/ui';

type Props = Omit<ComponentProps<typeof Input>, 'value' | 'defaultValue'> & {
  isCustomElement?: boolean;
  value?: string | number | null;
  decimalScale?: number;
  isAdditionalOnChange?: boolean;
};

const NumberInput = ({
  decimalScale = NUMERIC_CONFIG.precision.default,
  onChange,
  isCustomElement,
  ...rest
}: Props) => {
  const handleChange = (amountString: string) => {
    onChange?.({
      target: {
        value: amountString
          .replace(
            NUMERIC_CONFIG.decimal.display,
            NUMERIC_CONFIG.decimal.internal
          )
          .replaceAll(NUMERIC_CONFIG.thousandSeparator, ''),
      },
    } as never);
  };

  return (
    <NumericFormat
      {...rest}
      type={'text'}
      getInputRef={(el: HTMLInputElement) => {
        if (el && typeof el.focus !== 'function') {
          return;
        }
      }}
      allowNegative={false}
      allowLeadingZeros={false}
      thousandSeparator={NUMERIC_CONFIG.thousandSeparator}
      decimalSeparator={NUMERIC_CONFIG.decimal.display}
      onChange={e => handleChange(e.target.value)}
      onClear={() => {
        handleChange('');
      }}
      onPaste={e => {
        e.preventDefault();

        const data = e.clipboardData.getData('text').trim();

        handleChange(data);
      }}
      decimalScale={decimalScale}
      customInput={isCustomElement ? Input : undefined}
      isAllowed={values => {
        const [intPart, decimalPart] = values.value.split('.');
        return (
          intPart?.length <= NUMERIC_CONFIG.limits.maxIntegerDigits ||
          decimalPart?.length <= decimalScale
        );
      }}
    />
  );
};

NumberInput.displayName = 'NumberInput';

export { NumberInput };
